// src/services/chatApi.js
import i18n from 'i18next';

// Helper to get authentication headers
const getAuthHeaders = () => {
    const accessToken = localStorage.getItem('access_token');
    
    // Always include Content-Type for consistency in POST/DELETE requests.
    const headers = {
        'Content-Type': 'application/json',
    };

    if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
    } else {
        // This log is very useful for debugging.
        console.warn("Auth token not found in localStorage. Request will be sent without Authorization header.");
    }
    
    return headers;
};

export const getFormDataAuthHeaders = () => {
    const accessToken = localStorage.getItem('access_token');
    
    // Start with an empty headers object
    const headers = {};

    if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
    }
    
    // Notice: We NEVER add Content-Type here.
    // The browser will handle it for FormData.
    return headers;
};

// const handleResponse = async (response) => {
//     if (!response.ok) {
//         const errorData = await response.json().catch(() => ({
//             detail: `Request failed with status ${response.status}`
//         }));
//         throw new Error(errorData.detail || 'Request failed');
//     }
    
//     // For 204 No Content
//     if (response.status === 204) {
//         return null;
//     }
    
//     return response.json();
// };

// A new helper function to handle 401 errors globally
const handleUnauthorized = () => {
    // This is a robust way to handle expired sessions.
    console.error("Authentication failed (401). Session may be expired. Redirecting to login.");
    // Clear potentially bad tokens from storage.
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token'); // If you use refresh tokens
    // Redirect the user to the login page.
    window.location.href = '/login';
};


// const API_BASE_URL = 'http://localhost:8000/api/ai-tools';

// const API_USERS_URL = 'http://localhost:8000/api/users';

// const API_BASE_URL = '/api/ai-tools';
// const API_USERS_URL = '/api/users';

const DOMAIN = process.env.REACT_APP_API_URL || '';

// Construct the full URL: https://api.ugotai.com/api/ai-tools
const API_BASE_URL = `${DOMAIN}/ai-tools`;

let isRefreshing = false;
// This queue will hold all API calls that fail while a token refresh is in progress.
let failedQueue = [];

// --- Helper function to process the queue ---
const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error); // Reject all queued requests if refresh fails
        } else {
            prom.resolve(token); // Resolve all queued requests with the new token
        }
    });
    failedQueue = []; // Clear the queue
};




// A wrapper for fetch to handle errors consistently
export const apiFetch = async (url, options = {}) => {
    // 1. Prepare headers and add the current access token
    const token = localStorage.getItem('access_token');
    const headers = {
        'Content-Type': 'application/json',
        ...options.headers,
    };
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }

    // 2. Make the initial API call
    const response = await fetch(url, { ...options, headers });

    // 3. Check for a 401 Unauthorized error (expired access token)
    if (response.status === 401) {
        const refreshToken = localStorage.getItem('refresh_token');
        if (!refreshToken) {
            handleUnauthorized(); // No refresh token, must log out
            return Promise.reject(new Error('Unauthorized: No refresh token'));
        }

        // If a refresh is already happening, queue this request and wait
        if (isRefreshing) {
            return new Promise((resolve, reject) => {
                failedQueue.push({
                    resolve: (newToken) => {
                        // When the refresh is done, this will be called.
                        // We retry the original request with the new token.
                        const newOptions = { ...options, headers: { ...headers, 'Authorization': `Bearer ${newToken}` } };
                        resolve(apiFetch(url, newOptions)); // Recursively call apiFetch to re-process
                    },
                    reject,
                });
            });
        }

        // This is the first request to fail, so let's start the refresh process
        isRefreshing = true;

        try {
            // 4. Send the request to your refresh token endpoint
            // !!! IMPORTANT: Adjust this URL to match your Django project's refresh token URL !!!
            const refreshResponse = await fetch(`${API_BASE_URL}/users/token/refresh/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ refresh: refreshToken }),
            });

            if (!refreshResponse.ok) {
                // If the refresh token is invalid (e.g., expired or blacklisted), logout.
                throw new Error('Refresh token is invalid.');
            }

            const { access: newAccessToken } = await refreshResponse.json();
            
            // 5. Store the new token and process the queue
            localStorage.setItem('access_token', newAccessToken);
            processQueue(null, newAccessToken); // Process queued requests with the new token

            // 6. Retry the original failed request with the new token
            const newOptions = { ...options, headers: { ...headers, 'Authorization': `Bearer ${newAccessToken}` } };
            return apiFetch(url, newOptions); // Retry the original request

        } catch (error) {
            // If the refresh process fails, reject all queued requests and log out.
            processQueue(error, null);
            handleUnauthorized();
            return Promise.reject(error);
        } finally {
            // 7. ALWAYS unlock the refreshing state
            isRefreshing = false;
        }
    }

    // --- If the response was not 401, handle it normally ---
    if (!response.ok) {
        // This handles all other errors (400, 404, 500, etc.)
        const errorData = await response.json().catch(() => ({ detail: `Request failed with status ${response.status}` }));
        throw new Error(errorData.detail || 'An unknown API error occurred');
    }

    // For DELETE or other requests that might not return a JSON body
    if (response.status === 204) {
        return {}; // Return an empty object for consistency
    }

    return response.json(); // Return the parsed JSON for successful requests
};


export const summarizeVideo = async (videoFile) => {
    const language = i18n.language ? i18n.language.split('-')[0] : 'en';
    const formData = new FormData();
    formData.append('video', videoFile);


    const response = await fetch(`${API_BASE_URL}/video/video-tools/summarize/`, {
        method: 'POST',
        headers: {
                ...getFormDataAuthHeaders(),
                'Accept-Language': language
            },
        body: formData
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Upload failed');
    }
    return await response.json();
};

export const checkTaskStatus = async (taskId) => {
    const response = await fetch(`${API_BASE_URL}/video/tasks/status/${taskId}/`, {
        headers: getAuthHeaders()
    });

    if (!response.ok) {
        try {
            // Attempt to parse JSON error response
            const errorData = await response.json();
            throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
        } catch (jsonError) {
            // If JSON parsing fails, try to get text response for debugging
            try {
                const errorText = await response.text();
                console.error('Server error response:', errorText);
                throw new Error(`Server error (${response.status}): ${errorText.substring(0, 200)}...`);
            } catch (textError) {
                // If both JSON and text fail, throw generic error
                throw new Error(`HTTP error! status: ${response.status}`);
            }
        }
    }

    // For successful responses, handle potential JSON parsing errors
    try {
        return await response.json();
    } catch (jsonError) {
        console.error('Failed to parse JSON response:', response);
        throw new Error('Invalid JSON response from server');
    }
};

// extract text from video

export const transcribeVideo = async (videoFile) => {
    const language = i18n.language ? i18n.language.split('-')[0] : 'en';
    const formData = new FormData();
    formData.append('video', videoFile);

    const response = await fetch(`${API_BASE_URL}/video/video-tools/transcribe/`, {
        method: 'POST',
        headers: {
                ...getFormDataAuthHeaders(),
                'Accept-Language': language
            },
        body: formData
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Transcription upload failed');
    }
    return await response.json();
};

export const subtitleVideo = async (videoFile, targetLangCode, targetLangName, offset) => {
    const language = i18n.language ? i18n.language.split('-')[0] : 'en';
    const formData = new FormData();
    formData.append('video', videoFile);
    formData.append('target_language', targetLangCode);
    formData.append('target_language_name', targetLangName);
    formData.append('offset', offset);

    const response = await fetch(`${API_BASE_URL}/video/video-tools/subtitle/`, {
        method: 'POST',
        headers: {
                ...getFormDataAuthHeaders(),
                'Accept-Language': language
            },
        body: formData
    });

    if (!response.ok) {
        try {
            // Attempt to parse JSON error response
            const errorData = await response.json();
            throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
        } catch (jsonError) {
            // If JSON parsing fails, try to get text response for debugging
            try {
                const errorText = await response.text();
                console.error('Server error response:', errorText);
                throw new Error(`Server error (${response.status}): ${errorText.substring(0, 200)}...`);
            } catch (textError) {
                // If both JSON and text fail, throw generic error
                throw new Error(`HTTP error! status: ${response.status}`);
            }
        }
    }
    
    try {
        return await response.json();
    } catch (jsonError) {
        console.error('Failed to parse JSON response:', response);
        throw new Error('Invalid JSON response from server');
    }
};

export const addTextToVideo = async (videoFile, params) => {
    const language = i18n.language ? i18n.language.split('-')[0] : 'en';
    const formData = new FormData();
    formData.append('video', videoFile);
    formData.append('text', params.text);
    formData.append('font_size', params.fontSize);
    formData.append('text_color', params.textColor);
    formData.append('horizontal_position', params.horizontalPos);
    formData.append('vertical_position', params.verticalPos);
    formData.append('start_time', params.startTime);
    formData.append('end_time', params.endTime);

    const response = await fetch(`${API_BASE_URL}/video/video-tools/add-text-video/`, {
        method: 'POST',
        headers: {
                ...getFormDataAuthHeaders(),
                'Accept-Language': language
            },
        body: formData
    });

    if (!response.ok) {
        try {
            const errorData = await response.json();
            throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
        } catch (jsonError) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
    }
    
    return await response.json();
};


// powerpoint to video

export const analyzePpt = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await fetch(`${API_BASE_URL}/video/video-tools/ppt-analyze/`, {
        method: 'POST',
        headers: getFormDataAuthHeaders(), // Does not include Content-Type
        body: formData
    });
    
    if (!response.ok) throw new Error("Failed to analyze PPT");
    return await response.json();
};

export const pptToVideo = async (file, voice, silentSlidesArray) => {
    const language = i18n.language ? i18n.language.split('-')[0] : 'en';
    const formData = new FormData();
    formData.append('file', file);
    formData.append('voice', voice);
    // Convert array [1, 3] to string "1, 3"
    formData.append('silent_slides', silentSlidesArray.join(','));

    const response = await fetch(`${API_BASE_URL}/video/video-tools/ppt-to-video/`, {
        method: 'POST',
        headers: {
            ...getFormDataAuthHeaders(),
            'Accept-Language': language
        },
        body: formData
    });

    if (!response.ok) {
        try {
            const errorData = await response.json();
            throw new Error(errorData.error || "Upload failed");
        } catch {
            throw new Error("HTTP Error");
        }
    }
    
    return await response.json();
};

// apply filter

export const applyVideoFilter = async (videoFile, filterType, kernelSize) => {
    const language = i18n.language ? i18n.language.split('-')[0] : 'en';
    const formData = new FormData();
    formData.append('video', videoFile);
    formData.append('filter_type', filterType);
    if (filterType === 'Blur') {
        formData.append('kernel_size', kernelSize);
    }

    const response = await fetch(`${API_BASE_URL}/video/video-tools/apply-filter/`, {
        method: 'POST',
        headers: {
            ...getFormDataAuthHeaders(),
            'Accept-Language': language
        },
        body: formData
    });

    if (!response.ok) {
        try {
            const errorData = await response.json();
            throw new Error(errorData.error || "Request failed");
        } catch {
            throw new Error("HTTP Error");
        }
    }
    
    return await response.json();
};

// add audio to video

export const getDefaultAudioUrl = async () => {
    try {
        const response = await fetch(`${API_BASE_URL}/video/video-tools/get-default-audio-url/`, {
            method: 'GET',
            headers: getAuthHeaders()
        });
        
        console.log('Response status:', response.status);
        console.log('Response ok:', response.ok);
        
        const data = await response.json();
        console.log('Response data:', data);
        
        if (!response.ok) {
            throw new Error(data.error || "Failed to load default audio");
        }
        
        return data.url;
    } catch (error) {
        console.error('getDefaultAudioUrl error:', error);
        throw error;
    }
};


export const addAudioToVideo = async (videoFile, audioFile, volume, repeatAudio) => {
    const language = i18n.language ? i18n.language.split('-')[0] : 'en';
    const formData = new FormData();
    formData.append('video', videoFile);
    if (audioFile) {
        formData.append('audio', audioFile);
    }
    formData.append('volume', volume); // float
    formData.append('repeat_audio', repeatAudio); // boolean

    const response = await fetch(`${API_BASE_URL}/video/video-tools/add-audio-to-video/`, {
        method: 'POST',
        headers: {
                ...getFormDataAuthHeaders(),
                'Accept-Language': language
            },
        body: formData
    });

    if (!response.ok) {
        try {
            const errorData = await response.json();
            throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
        } catch (jsonError) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
    }
    
    return await response.json();
};

// remove audio from video

export const removeAudioFromVideo = async (videoFile) => {
    const language = i18n.language ? i18n.language.split('-')[0] : 'en';
    const formData = new FormData();
    formData.append('video', videoFile);

    // Assuming you set up the URL in Django urls.py
    const response = await fetch(`${API_BASE_URL}/video/video-tools/remove_audio_from_video/`, {
        method: 'POST',
        headers: {
                ...getFormDataAuthHeaders(),
                'Accept-Language': language
            },
        body: formData
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Audio extraction upload failed');
    }
    return await response.json();
};


// video to audio

export const extractAudio = async (videoFile) => {
    const language = i18n.language ? i18n.language.split('-')[0] : 'en';
    const formData = new FormData();
    formData.append('video', videoFile);

    // Assuming you set up the URL in Django urls.py
    const response = await fetch(`${API_BASE_URL}/video/video-tools/extract-audio/`, {
        method: 'POST',
        headers: {
                ...getFormDataAuthHeaders(),
                'Accept-Language': language
            },
        body: formData
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Audio extraction upload failed');
    }
    return await response.json();
};

// video to image

export const extractFrame = async (videoFile, timestamp) => {
    const language = i18n.language ? i18n.language.split('-')[0] : 'en';
    const formData = new FormData();
    formData.append('video', videoFile);
    formData.append('timestamp', timestamp); // Send timestamp

    const response = await fetch(`${API_BASE_URL}/video/video-tools/extract-frame/`, {
        method: 'POST',
        headers: {
                ...getFormDataAuthHeaders(),
                'Accept-Language': language
            },
        body: formData
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Frame extraction upload failed');
    }
    return await response.json();
};

// convert video format

export const convertVideo = async (videoFile, targetFormat) => {
    const language = i18n.language ? i18n.language.split('-')[0] : 'en';
    const formData = new FormData();
    formData.append('video', videoFile);
    formData.append('target_format', targetFormat);

    const response = await fetch(`${API_BASE_URL}/video/video-tools/convert/`, {
        method: 'POST',
        headers: {
                ...getFormDataAuthHeaders(),
                'Accept-Language': language
            },
        body: formData
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Conversion upload failed');
    }
    return await response.json();
};
